/**
 * Algorithmically shorten holiday names for compact display.
 * Inspired by getNameBadge but optimized for holiday names.
 *
 * Strategy:
 * 1. Remove common suffixes like "Day" (e.g., "Christmas Day" → "Christmas")
 * 2. Abbreviate common words: "Year" → "yr", "Saint" → "St"
 * 3. If still too long, apply word-initials or consonant stripping
 * 4. Result is lowercase for subtle background display
 *
 * @param name - Full holiday name
 * @param maxLength - Maximum length target (default 12)
 * @returns Shortened name
 *
 * Examples:
 * - "New Year's Day" → "new yrs"
 * - "Thanksgiving Day" → "thanksgiving"
 * - "Independence Day" → "independence"
 * - "Martin Luther King Jr. Day" → "mlk jr"
 * - "Christmas Day" → "christmas"
 * - "Washington's Birthday" → "washington's bday"
 * - "Labor Day" → "labor"
 */
export function shortenHolidayName(
  name: string,
  maxLength: number = 12
): string {
  if (!name) return '';

  const trimmed = name.trim();

  // No shortening needed — return as-is
  if (trimmed.length <= maxLength) return trimmed;

  let shortened = trimmed;

  // Common replacements (case-insensitive, preserve rest of word's case)
  const replacements: [RegExp, string][] = [
    [/\bDay\b/gi, ''], // Remove "Day" suffix
    [/\bYear('s)?\b/gi, 'yr$1'], // "Year" or "Year's" → "yr" or "yr's"
    [/\bSaint\b/gi, 'St'], // "Saint" → "St"
    [/\bBirthday\b/gi, 'bday'], // "Birthday" → "bday"
    [/\bPresident's\b/gi, "Pres'"], // "President's" → "Pres'"
  ];

  for (const [pattern, replacement] of replacements) {
    shortened = shortened.replace(pattern, replacement);
  }

  // Clean up extra spaces from removed words
  shortened = shortened.replace(/\s+/g, ' ').trim();

  // If still too long, try initials for multi-word names
  if (shortened.length > maxLength) {
    const words = shortened.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      // Use first letter of each word for long words (>3 chars)
      const initials = words
        .map((word, i) => {
          // Keep short words as-is, use initial for long words
          if (word.length <= 3 || i === words.length - 1) {
            return word.toLowerCase();
          }
          // For names like "Martin Luther King", take first letter of first words
          return word[0].toLowerCase();
        })
        .join(' ');

      if (initials.length <= maxLength) {
        shortened = initials;
      }
    }
  }

  // If still too long, strip vowels from the longest word
  if (shortened.length > maxLength) {
    const words = shortened.split(/\s+/).filter(Boolean);
    const longestIndex = words.reduce(
      (maxIdx, word, i) =>
        word.length > words[maxIdx].length ? i : maxIdx,
      0
    );

    const longestWord = words[longestIndex];
    const withoutVowels = longestWord
      .replace(/[aeiou]/gi, '')
      .toLowerCase();

    // Keep at least 3 consonants or the maxLength
    const minKeep = Math.min(3, maxLength);
    const keptVowelStripped =
      withoutVowels.length >= minKeep
        ? withoutVowels.slice(0, maxLength)
        : longestWord.slice(0, maxLength).toLowerCase();

    words[longestIndex] = keptVowelStripped;
    shortened = words.join(' ');
  }

  // Final fallback: hard truncate with ellipsis if still too long
  if (shortened.length > maxLength) {
    shortened =
      shortened.slice(0, maxLength - 1).toLowerCase() + '…';
  }

  return shortened;
}
